import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Check, X, Phone, Mail, Car } from "lucide-react";

const MAX_VEHICLE_AGE = 4;

/** Dispatcher → Applications tab: review "Drive with us" driver applications.
 *  Approve = create driver profile + attach docs + auto-send onboarding packet
 *  (W-9 + contract) and Stripe bank link. Decline archives it. */
export const DriverApplicationsTab = () => {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/driver-applications").then(r => setApps(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (a) => {
    if (!window.confirm(`Approve ${a.full_name}? This creates their driver profile, texts/emails them the good news, and sends the onboarding packet (W-9 + contract) plus the bank setup link.`)) return;
    setBusyId(a.app_id);
    try {
      const r = await api.post(`/admin/driver-applications/${a.app_id}/approve`);
      toast.success(`${a.full_name} approved — onboarding packet${r.data.bank_link_sent ? " + bank link" : ""} sent`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally { setBusyId(null); }
  };

  const decline = async (a) => {
    const reason = window.prompt(`Decline ${a.full_name}'s application? They'll get a polite SMS + email (without the reason). Optional internal reason:`);
    if (reason === null) return;
    setBusyId(a.app_id);
    try {
      await api.post(`/admin/driver-applications/${a.app_id}/decline`, { reason });
      toast.success("Application declined");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline");
    } finally { setBusyId(null); }
  };

  const shown = apps.filter(a => filter === "all" || a.status === filter);
  const pendingCount = apps.filter(a => a.status === "pending").length;

  return (
    <section className="pb-20 px-6 md:px-12" data-testid="applications-tab">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <h2 className="font-serif text-2xl">Driver applications {pendingCount > 0 && <span className="text-[#D4AF37] text-lg">· {pendingCount} pending</span>}</h2>
          <div className="flex gap-2">
            {["pending", "approved", "declined", "all"].map(k => (
              <button key={k} data-testid={`apps-filter-${k}`} onClick={() => setFilter(k)}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.18em] border ${filter === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>
                {k}
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center text-sm text-[#71717a]" data-testid="apps-empty">
            No {filter === "all" ? "" : filter + " "}applications. New applicants apply at /drive-with-us (footer link "Driver / Dispatch").
          </div>
        ) : (
          <div className="space-y-4">
            {shown.map(a => <ApplicationCard key={a.app_id} a={a} busy={busyId === a.app_id} onApprove={() => approve(a)} onDecline={() => decline(a)}/>)}
          </div>
        )}
      </div>
    </section>
  );
};

const DOC_LABELS = { license: "Driver's license", insurance: "Certificate of insurance", chauffeur: "Chauffeur / limo license" };

function ApplicationCard({ a, busy, onApprove, onDecline }) {
  const yearOk = a.vehicle_year >= new Date().getFullYear() - MAX_VEHICLE_AGE;
  const badge = {
    pending: "bg-[#D4AF37]/15 text-[#D4AF37]",
    approved: "bg-emerald-500/15 text-emerald-400",
    declined: "bg-red-500/15 text-red-400",
  }[a.status] || "bg-white/10 text-white";

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`application-${a.app_id}`}>
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-[#F5F2EB]" data-testid="app-card-name">{a.full_name}</h3>
            <span className={`px-2.5 py-1 text-[9px] uppercase tracking-[0.18em] rounded ${badge}`} data-testid="app-card-status">{a.status}</span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-[#A1A1AA]">
            <span className="flex items-center gap-1.5"><Phone size={12}/> <a href={`tel:${a.phone}`} className="hover:text-[#D4AF37]">{a.phone}</a></span>
            <span className="flex items-center gap-1.5"><Mail size={12}/> {a.email}</span>
            <span className="flex items-center gap-1.5">
              <Car size={12}/> {a.vehicle_year} {a.vehicle_make} {a.vehicle_model} {a.vehicle_color}
              {!yearOk && <span className="text-red-400 font-semibold ml-1">— over {MAX_VEHICLE_AGE} yrs old</span>}
            </span>
          </div>
          <div className="text-[10px] text-[#52525B] mt-1.5">
            Applied {new Date(a.created_at).toLocaleString()}
            {a.driver_id && <> · driver {a.driver_id}</>}
            {a.decline_reason && <> · reason: {a.decline_reason}</>}
          </div>
        </div>
        {a.status === "pending" && (
          <div className="flex gap-2">
            <button data-testid="app-approve-btn" disabled={busy} onClick={onApprove}
              className="btn-gold !py-2 !px-4 text-[10px] flex items-center gap-1.5"><Check size={13}/> Approve &amp; onboard</button>
            <button data-testid="app-decline-btn" disabled={busy} onClick={onDecline}
              className="border border-red-500/40 text-red-400 px-4 py-2 text-[10px] uppercase tracking-[0.16em] hover:bg-red-500/10 disabled:opacity-50 flex items-center gap-1.5"><X size={13}/> Decline</button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {Object.entries(a.files || {}).map(([kind, fid]) => (
          <button key={fid} data-testid={`app-doc-${kind}`}
            onClick={() => window.open(`${API}/files/${fid}/content`, "_blank")}
            className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50">
            <FileText size={12}/> {DOC_LABELS[kind] || kind}
          </button>
        ))}
        {!a.files?.chauffeur && <span className="text-[10px] text-[#52525B] self-center">Chauffeur / limo license: not provided yet</span>}
      </div>
    </div>
  );
}
